import React from 'react';
import { Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import LaContractTask from '@/components/common/LaContractTask';

// The address is the identity: a student who accepted as an applicant and as
// a nominee is one LA. Entries with no address are kept, each its own.
export function acceptedEntries(candidacies, nominations) {
    const seen = new Set();
    const entries = [];
    const add = (entry) => {
        const key = entry.email ? entry.email.toLowerCase() : null;
        if (key) {
            if (seen.has(key)) return;
            seen.add(key);
        }
        entries.push(entry);
    };
    for (const candidacy of candidacies || []) {
        if (candidacy.stage === 'accepted') {
            add({
                id: candidacy.id,
                name: candidacy.student?.name,
                email: candidacy.student?.email,
            });
        }
    }
    for (const nomination of nominations || []) {
        if (nomination.status === 'accepted') {
            add({ name: nomination.name, email: nomination.email });
        }
    }
    return entries;
}

/**
 * The students who have accepted a place on one position, with the address
 * the reviewer may write to. An applicant's name opens their record (to
 * release them, or read their application); a named student has none here.
 */
export default function YourTeam({
    candidacies,
    nominations,
    mode,
    headingLevel = 3,
    selectedId,
    hrefFor,
    onSelect,
    emptyClassName = '',
}) {
    const Heading = `h${Math.min(headingLevel, 6)}`;
    const entries = acceptedEntries(candidacies, nominations);
    const title = mode === 'coordinator' ? 'Accepted' : 'Your team';
    if (entries.length === 0) {
        return (
            <p className={`mb-3 small ${emptyClassName}`}>
                <span className="fw-semibold">{`${title}: `}</span>
                no one has accepted yet. Students who accept an offer appear
                here.
            </p>
        );
    }
    return (
        <div className="mb-3">
            <Heading className="h6 mb-1">{title}</Heading>
            <ul className="list-unstyled mb-0">
                {entries.map((entry, index) => {
                    const current =
                        entry.id && entry.id === selectedId
                            ? 'true'
                            : undefined;
                    let nameNode = (
                        <span className="fw-semibold">{entry.name}</span>
                    );
                    if (entry.id && hrefFor) {
                        nameNode = (
                            <Link
                                to={hrefFor(entry.id)}
                                className="fw-semibold"
                                aria-current={current}
                                onClick={(event) => onSelect?.(entry.id, event)}
                            >
                                {entry.name}
                            </Link>
                        );
                    } else if (entry.id && onSelect) {
                        nameNode = (
                            <Button
                                variant="link"
                                className="p-0 fw-semibold align-baseline"
                                aria-current={current}
                                onClick={(event) => onSelect(entry.id, event)}
                            >
                                {entry.name}
                            </Button>
                        );
                    }
                    return (
                        <li
                            key={`${entry.email || entry.name}-${index}`}
                            className="text-break"
                        >
                            {nameNode}
                            {entry.email && (
                                <>
                                    {', '}
                                    <a href={`mailto:${entry.email}`}>
                                        {entry.email}
                                    </a>
                                </>
                            )}
                            {mode !== 'coordinator' && (
                                <LaContractTask name={entry.name} />
                            )}
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}
